import { useMemo, useState, useEffect, useCallback } from 'react';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';

export default function useSubjectItemsSelector({
  items: allItems,
  loading,
  submitting,
  onConfirmSelection,
  title,
  confirmButtonTitle,
}) {
  const [selectedItemIds, setSelectedItemIds] = useState({});

  const items = allItems;

  const sections = useMemo(() => {
    if (!items || items.length === 0) return [];

    const groupedItems = groupBy(items, 'subject._id');
    const allSubjects = Array.from(new Set(items.map((d) => d.subject._id)));

    return allSubjects.map((subjectId) => {
      const subjectItems = groupedItems[subjectId];
      const [firstItem] = subjectItems;

      return {
        title: firstItem?.subject?.name,
        items: groupedItems[subjectId],
      };
    });
  }, [items]);

  const isItemSelected = useCallback(
    (item) => {
      return !!selectedItemIds[item._id];
    },
    [selectedItemIds]
  );

  useEffect(() => {
    if (!isEmpty(selectedItemIds)) {
      setSelectedItemIds({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems]);

  const selectedItems = useMemo(() => {
    return allItems.filter(isItemSelected);
  }, [allItems, isItemSelected]);

  const allItemsSelected = useMemo(() => {
    return items.filter(isItemSelected).length === items.length;
  }, [items, isItemSelected]);

  const noItemsSelected = useMemo(() => {
    return items.filter(isItemSelected).length === 0;
  }, [items, isItemSelected]);

  const onToggleItem = useCallback((item) => {
    setSelectedItemIds((data) => ({
      ...data,
      [item._id]: !data[item._id],
    }));
  }, []);

  const handleConfirmSelection = useCallback(() => {
    onConfirmSelection(selectedItems);
  }, [selectedItems, onConfirmSelection]);

  return {
    loading,
    sections,
    selectedItems,
    allItemsSelected,
    noItemsSelected,
    title,
    confirmButtonTitle,
    submitting,

    isItemSelected,
    onToggleItem,
    onConfirmSelection: handleConfirmSelection,
  };
}
